/*
 * 戒导航 · 戒打卡独立页（/checkin）
 * =============================================================================
 * 设计依据：doc/戒打卡功能设计方案.md §18.2（页面骨架）、§18.4（多目标）、
 *           §18.5（时间线）、§18.6（统计卡）、§18.8（通知入口无需在此处理）。
 *
 * 页面骨架（§18.2）：
 *   CheckinPage
 *    ├─ Header        目标切换器（多目标时显示）+「新建目标」+ 设置入口
 *    ├─ StatsCards    当前连续 / 最长连续 / 累计成功 / 本月成功 / 成功率
 *    ├─ CalendarCard  大月历：翻月 / 回今天；点击某日 → 当日详情（编辑心情/备注/撤销）
 *    ├─ TimelineCard  记录时间线（按日倒序；LoadingIndicator + 自绘分页条）
 *    └─ EmptyState    无目标时 Placeholder + 引导（自动创建默认目标）
 *
 * ★★ 数据通道（关键，别绕过）★★
 *   独立页走**标准 JSON:API 资源**（app.store.find/createRecord + Model.save），
 *   与首页内联脚本走的 forum 栈自建接口是**两条通道**，但共用同一个
 *   CheckinService，业务规则单点实现（§18.2「数据来源」、§5 约束 5）。
 *   唯一例外：首次访问且**尚无任何目标**时，用一个 GET 调 forum 栈的
 *   `/checkin/summary`（不传 habitId）触发 Service 的「惰性创建默认目标」，
 *   随后刷新 JSON:API 列表 —— 这样避免为引导流程新增后端路由（复用既有能力）。
 *
 * ★★ 复用清单（不重复造轮子，§9.4）★★
 *   Page / LoadingIndicator / Placeholder / Button / Icon 全部来自核心；
 *   月历为纯 CSS Grid 自绘（§9.2），不引入任何图表库。
 *
 * ⚠️ 为什么**不用**核心的 flarum/common/components/Pagination（重要踩坑记录）★
 *   核心把 Pagination 只注册进 **admin bundle**（`flarum.reg.add('core',
 *   'common/components/Pagination', ...)` 仅出现在 vendor/flarum/core/js/dist/admin.js），
 *   论坛端（forum/common bundle）**没有**这个注册项。因此在 forum 侧写
 *   `import Pagination from 'flarum/common/components/Pagination'` 会在模块 eval 期
 *   变成 `flarum.reg.get('core','common/components/Pagination')` → 抛
 *   「No module found」→ 该扩展的 IIFE 中断 → 聚合产物 forum.js 中**其后所有扩展**
 *   的顶层注册语句全部不执行（曾经导致 fof/blog 未注册、顶栏「博客」入口消失）。
 *   解决：本页时间线分页改为**自绘极简分页条**（上一页 / 第 x / y 页 / 下一页），
 *   与 packages/review 的 ReviewList.pager() 同一范式，零核心依赖。
 *
 * ★★ 响应式（§18.2）★★
 *   与首页同三断点：≥1200（多列）/ ≤1200（收窄）/ ≤860（单列、月历格 ≥40px、
 *   时间线全宽）。具体数值落在 less/forum.less，本组件只用语义 class 承载结构。
 *
 * ★★ i18n（§18.2）★★
 *   所有可见文案一律走 app.translator.trans('jiedh-checkin.forum.*')，
 *   对应 resources/locale/{zh-Hans,en}.yml，禁止硬编码中文。
 *
 * ★★ 日期库 dayjs 为什么**不 import**（构建踩坑记录）★★
 *   核心 bundle 已用 expose-loader 把 dayjs 挂到 window
 *   （vendor/flarum/core/js/src/common/index.ts L4），并在
 *   vendor/flarum/core/js/src/@types/global.d.ts L60 声明了全局 `dayjs`。
 *   扩展侧 `import dayjs from 'dayjs'` 会去 node_modules 找包 → webpack 报
 *   「Module not found: dayjs」（本扩展 js/package.json 刻意不引入该依赖，
 *   避免与核心打包一份重复副本）。因此这里与全局 `m` 一样，直接用全局 dayjs。
 */
import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import Icon from 'flarum/common/components/Icon';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Placeholder from 'flarum/common/components/Placeholder';
import extractText from 'flarum/common/utils/extractText';
import { STATUS_SUCCESS } from '../../common/models/CheckinRecord';
/*
 * 「正在分享中」进度浮层（★ 2026-10-03 响应用户反馈，实现见该文件头注释）。
 *
 * ★ 为什么是**静态** import 而不是懒加载（动态 import 带路径参数的写法）：
 *   浮层要在「关弹窗 → 出图 → 上传 → 建帖」这条**同步起步**的链路上第一时间出现，
 *   动态 chunk 还要先走一次网络；而本项目已有判例（踩坑 P43 / P38、FQ-076）表明
 *   checkin 侧的动态 chunk 会产出匿名数字文件、给发布与注册带来额外风险。
 *   本模块极小（一个单例 + 核心 LoadingIndicator），打进主 bundle 代价可忽略。
 *
 * ⚠️ 上面这条注释**绝不能**写成带引号路径的动态 import 字面量：
 *   `flarum-webpack-config` 的 autoChunkNameLoader 会连**注释里**的这种写法一起改写，
 *   往注释里插一条 webpack magic comment ⇒ 块注释被提前闭合 ⇒ webpack 报
 *   「Module parse failed: Unexpected token」并且凭空多出一个找不到 chunk 的懒加载请求。
 */
import { hidePublishProgress, setPublishProgress, showPublishProgress } from './PublishProgress';

/** 时间线每页条数（与后端 CheckinRecordResource::paginate(30) 对齐，§18.3）。 */
const PAGE_SIZE = 30;

/**
 * 月历单月最多取多少条记录（当月最多 31 天，取 31 足够覆盖；§18.5 数据源同源）。
 * 用 page[limit] 覆盖资源默认的 30，避免 31 天的月份漏掉最后一天。
 */
const MONTH_FETCH_LIMIT = 31;

/**
 * 心情取值范围（1–5），与后端 CheckinService 的归一化区间一致。
 * 0 表示「未设置」（前端不提交 mood 字段）。
 */
const MOOD_VALUES = [1, 2, 3, 4, 5];

/**
 * 年度热力图一次性拉取的记录上限（§19.3：单用户一年 ≤ 366 行，一次查询）。
 * 后端 CheckinRecordResource 已把 Index 的 maxLimit 放宽到 400
 * （见 src/Api/Resource/CheckinRecordResource.php 的 paginate(30, 400)），
 * 这里取 400 正好一次覆盖整年（含闰年 366 天）。
 */
const YEAR_FETCH_LIMIT = 400;

/** 月历 42 格的日期格结构（纯前端自绘，§9.2）。 */

/**
 * 年度热力图单格（§19.3，纯前端自绘 GitHub 风格）。
 * 与月历不同，热力图**不可点击**（仅呈现年度概览），因此不承载交互状态。
 */

/**
 * 年度热力图的**唯一**分桶口径：「截至当日的连续天数」→ 热力等级 0–4。
 *
 * ★ 为什么抽成模块级函数（2026-10-03，需求「把年度打卡放进海报中」）：
 *   同一份数据现在有**两个消费者** —— 网页版热力图（`buildHeatmapCells()`）与
 *   分享海报（`buildYearHeatmapLevels()` → share 侧绘制）。若两处各写一遍阈值，
 *   改一处忘另一处就会出现「网页 4 级、海报 3 级」的口径漂移。
 *   这类「同一业务口径两处实现」的坑本项目已经踩过（§7.3.3 海报字号口径漂移），
 *   故此处收口为单一来源。
 *
 * @param run 截至当日的连续成功天数（0 = 当日未成功 / 或刚被破戒打断）
 * @returns 0 = 无成功；1 = 连续 1–2 天；2 = 3–6 天；3 = 7–29 天；4 = ≥30 天
 */
function heatmapLevelForRun(run) {
  if (!(run > 0)) return 0;
  if (run >= 30) return 4;
  if (run >= 7) return 3;
  if (run >= 3) return 2;
  return 1;
}

/**
 * 前台配置对象（= 后端 ForumCheckinConfig 序列化出的 `jiedhCheckinConfig`）。
 *
 * ★ 为什么不用散落的设置键：论坛前台 SPA 拿不到 settings 表的值，
 *   后端只把「聚合对象」经 Extend\Settings()->serializeToForum() 发下来
 *   （范式与 packages/share 的 `jiedhShareConfig` 一致，见 extend.php 注释）。
 * ★ 字段「只增不改」：所有字段都按可选读取并给兜底，避免后端灰度期读到 undefined。
 */

/** 合法色卡 key 白名单（与 share 的 PALETTE_KEYS 同源口径，此处只做最小兜底校验）。 */
const CHECKIN_PALETTE_KEYS = ['A', 'B', 'C', 'D'];

/**
 * 读取并规范化前台配置（每次读都做兜底，保证拿到的永远是可用值）。
 * 关闭态（enabled === false）由后端把守，这里只做前端体验层判断。
 *
 * ★ 兜底方向与后端 `ForumCheckinConfig::__invoke()` 严格对照（否则会出现
 *   「PHP 认为该开、前端认为该关」的诡异表现）：
 *   · 布尔开关：用 `!== false`（缺省落到「开」，与 PHP 默认值 1 一致）；
 *   · `sharePostEnabled` 例外：用严格 `=== true`（缺省落到「关」，与 PHP 默认值 0 一致）；
 *   · `remarkReview.enabled` 同样严格 `=== true`（保守兜底，见接口注释）。
 */
function checkinConfig() {
  const raw = app.forum.attribute('jiedhCheckinConfig') ?? {};
  return {
    enabled: raw.enabled !== false,
    multiHabit: raw.multiHabit === true,
    maxHabits: Number(raw.maxHabits ?? 5) || 5,
    allowUndo: raw.allowUndo !== false,
    exportEnabled: raw.exportEnabled !== false,
    // ---- 分享系列 ----
    shareEnabled: raw.shareEnabled !== false,
    sharePosterEnabled: raw.sharePosterEnabled !== false,
    shareRemarkEnabled: raw.shareRemarkEnabled !== false,
    sharePostEnabled: raw.sharePostEnabled === true,
    sharePalette: CHECKIN_PALETTE_KEYS.indexOf(raw.sharePalette) >= 0 ? raw.sharePalette : 'A',
    sharePostTags: typeof raw.sharePostTags === 'string' ? raw.sharePostTags : '',
    sharePostCategory: Number(raw.sharePostCategory ?? 0) || 0,
    remarkReview: {
      enabled: raw.remarkReview?.enabled === true
    }
  };
}

/* ═══════════════════════════════════════════════════════════════════════════
 * 跨扩展 API `window.jiedhShare` 的**最小契约镜像**（★ 2026-10-03）
 * ═══════════════════════════════════════════════════════════════════════════
 * 依据：doc/戒打卡分享重设计方案.md §4.1.2。
 * 本项目各扩展前端**独立 webpack 编译**，戒打卡无法 import share 的源码/类型，
 * 只能按「结构类型」在本侧复刻一份等价契约（shape 由 share 的 shared/types.ts 定义，
 * 字段只增不改）。真实调用前一律先 `window.jiedhShare?.can('open')` 探测，
 * 缺失即回退旧的分享路径（§4.1.3 降级要求）。
 *
 * ⚠️ 本段全是**类型声明**，babel 转译时整段擦除，不产生任何运行时依赖 / 体积。
 */

/** 成就卡统计（与 share 的 `AchievementStats` 逐字段对齐，只承载聚合信息）。 */

/** 注入渠道的**运行时上下文**（与 share 的 `InjectedChannelContext` 逐字段对齐，P3 新增）。 */

/** 注入渠道定义（与 share 的 `InjectedChannelDef` 逐字段对齐；本期 id = `post`（预填）/ `postDirect`（直发））。 */

/** 打开分享面板的描述符（与 share 的 `ShareDescriptor` 逐字段对齐）。 */

/** `window.jiedhShare` 的能力契约（本侧只用到 `can` / `open`）。 */

/**
 * 打卡内容组件（承载全部业务逻辑与渲染方法）。
 *
 * ★ 为什么拆成 Component 而不是直接继承 Page（方案 A，2026-09-30）★
 *   同一套打卡内容需要同时服务两个落点：
 *     ① 独立页 `/checkin`（CheckinPage extends Page，本文件默认导出）；
 *     ② 个人主页 tab `/u/:username/checkin`（UserCheckinPage extends UserPage，
 *        见 ./UserCheckinPage.tsx），复用本组件。
 *   Page 会在 oninit 里写 `app.current`（routeName）、oncreate 里改 bodyClass /
 *   滚动位置，**一个路由只能有一个 Page 壳**；因此把内容抽成纯 Component，
 *   由各自的 Page/UserPage 外壳包裹，避免双重 Page 导致的 `app.current` 污染。
 *
 * ★ 路由无关性 ★
 *   本组件不读 routeName 决定渲染；唯一读取路由的地方是 `m.route.param('habit')`
 *   （query 参数，两种路由下都可用）。切换目标时是否同步 URL 由 `selectHabit()`
 *   按当前 routeName 判断，避免在个人主页 tab 下把用户"甩"到独立页。
 */
export class CheckinContent extends Component {
  // ---- 加载态 ----
  loadingHabits = true;
  loadingRecords = false;
  loadingMonth = false;
  /** 年度热力图加载中（§19.3） */
  loadingHeatmap = false;
  /** 提交中（打卡 / 撤销 / 保存详情 / 新建目标）时置真，用于禁用按钮防重复提交 */
  busy = false;

  // ---- 数据 ----
  /** 当前用户「未归档」的目标列表（归档目标不进切换器） */
  habits = [];
  /** 当前选中目标 id（null = 尚未选定 / 无目标） */
  currentHabitId = null;

  /** 时间线当前页记录 */
  records = [];
  /** 时间线总条数（取自 app.store.find 返回数组的 payload.meta.page.total） */
  recordsTotal = 0;
  /** 时间线当前页码（从 1 开始） */
  page = 1;

  /** 月历游标（指向展示月份的第一天） */
  monthCursor = (() => dayjs().startOf('month'))();
  /** 当月记录映射：key = 'YYYY-MM-DD' → 记录（用于月历着色与当日详情） */
  monthRecords = {};

  /**
   * 本年度记录映射：key = 'YYYY-MM-DD' → 记录（供年度热力图着色，§19.3）。
   * 与 monthRecords 分属两个数据集：热力图需整年（≤366 条），月历只需当月，
   * 分开缓存避免翻月时把整年数据也重取一遍。
   */
  yearRecords = {};

  // ---- 交互态 ----
  /** 当前展开「当日详情」的日期（null = 未展开） */
  activeDate = null;
  /** 是否显示「新建目标」内联表单（多目标开启时才可能为真） */
  showNewHabitForm = false;
  /** 新建目标表单：名称 */
  newHabitName = '';
  /** 新建目标表单：图标（emoji 文本，零依赖，§18.4） */
  newHabitIcon = '';
  /** 详情面板正在编辑的心情（0 = 未设置） */
  detailMood = 0;
  /** 详情面板正在编辑的备注 */
  detailNote = '';
  /** 全局错误提示文案键（i18n 键后缀或后端返回的 detail 原文） */
  error = null;
  oninit(vnode) {
    super.oninit(vnode);

    // 未登录：直接给出游客引导（不发起任何数据请求）
    if (!app.session.user) {
      this.loadingHabits = false;
      return;
    }
    this.monthCursor = dayjs().startOf('month');
    this.loadHabits();
  }

  // =========================================================================
  // 数据加载
  // =========================================================================

  /**
   * 加载目标列表并选定当前目标。
   *
   * 选定优先级（§18.4 切换器）：URL ?habit= > localStorage > 默认目标 > 第一个。
   * 若无任何目标（首次访问独立页）→ 调 forum 的 summary 触发惰性建默认目标，再重载。
   */
  async loadHabits() {
    this.loadingHabits = true;
    this.error = null;
    m.redraw();
    try {
      const all = await app.store.find('jiedh-checkin-habits');

      // 归档目标不进入切换器（但仍可查看历史，P2 再加"显示已归档"开关）
      this.habits = all.filter(h => !h.isArchived());
      if (this.habits.length === 0) {
        // 首次访问：借 forum 栈的 summary（不传 habitId）惰性创建默认目标
        await this.bootstrapDefaultHabit();
        const retry = await app.store.find('jiedh-checkin-habits');
        this.habits = retry.filter(h => !h.isArchived());
      }
      this.currentHabitId = this.resolveInitialHabitId(this.habits);
      if (this.currentHabitId) {
        await Promise.all([this.loadRecords(), this.loadMonth(), this.loadYear()]);
      }
    } catch (e) {
      this.error = this.errorMessage(e);
    } finally {
      this.loadingHabits = false;
      m.redraw();
    }
  }

  /**
   * 首次访问的默认目标引导。
   *
   * 复用既有能力：GET /checkin/summary（forum 栈，不传 habitId）→ 控制器
   * 经 resolveHabitId → Service::resolveDefaultHabit() 自动落库默认目标。
   * 这样无需为「独立页首次进入」新增后端路由。
   */
  async bootstrapDefaultHabit() {
    const base = app.forum.attribute('baseUrl');
    await app.request({
      method: 'GET',
      url: base + '/checkin/summary'
    });
    // summary 返回的是 forum 信封（{ok,data}），仅用于触发副作用，不消费其数据。
  }

  /**
   * 决定初始选中的目标 id。
   *
   * @param habits 未归档目标列表
   */
  resolveInitialHabitId(habits) {
    if (habits.length === 0) return null;

    // ① URL ?habit=（可分享/可刷新保持）
    const urlHabit = Number(m.route.param('habit'));
    if (urlHabit > 0 && habits.some(h => Number(h.id()) === urlHabit)) {
      return urlHabit;
    }

    // ② localStorage 记忆（移动端切换后无 URL 也能记住）
    const stored = Number(window.localStorage.getItem('jiedh-checkin.habit'));
    if (stored > 0 && habits.some(h => Number(h.id()) === stored)) {
      return stored;
    }

    // ③ 默认目标
    const def = habits.find(h => h.isDefault());
    if (def) return Number(def.id());

    // ④ 兜底：第一个
    return Number(habits[0].id());
  }

  /**
   * 加载当前目标的时间线（分页，§18.5）。
   *
   * 分页总数：app.store.find 返回的模型数组带 `.payload` 属性（Store.pushPayload
   * 会把原始载荷挂上去），总数在 `payload.meta.page.total`。
   */
  async loadRecords() {
    if (!this.currentHabitId) return;
    this.loadingRecords = true;
    m.redraw();
    try {
      const offset = (this.page - 1) * PAGE_SIZE;
      const result = await app.store.find('jiedh-checkin-records', {
        filter: {
          habitId: this.currentHabitId
        },
        sort: '-checkinDate',
        page: {
          offset,
          limit: PAGE_SIZE
        }
      });
      this.records = result;
      this.recordsTotal = Number(result.payload?.meta?.page?.total ?? result.length);
    } catch (e) {
      this.error = this.errorMessage(e);
      this.records = [];
      this.recordsTotal = 0;
    } finally {
      this.loadingRecords = false;
      m.redraw();
    }
  }

  /**
   * 加载当前展示月份的记录（供月历着色与当日详情，§18.5 同源数据）。
   *
   * 用 filter[from]/filter[to] 圈定当月首末，一次拉全月（≤31 条），
   * 翻月只重取数据、不整页刷新（§18.2 交互）。
   */
  async loadMonth() {
    if (!this.currentHabitId) return;
    this.loadingMonth = true;
    m.redraw();
    try {
      const from = this.monthCursor.startOf('month').format('YYYY-MM-DD');
      const to = this.monthCursor.endOf('month').format('YYYY-MM-DD');
      const result = await app.store.find('jiedh-checkin-records', {
        filter: {
          habitId: this.currentHabitId,
          from,
          to
        },
        sort: '-checkinDate',
        page: {
          limit: MONTH_FETCH_LIMIT
        }
      });
      const map = {};
      result.forEach(r => {
        map[r.checkinDate()] = r;
      });
      this.monthRecords = map;
    } catch (e) {
      this.error = this.errorMessage(e);
      this.monthRecords = {};
    } finally {
      this.loadingMonth = false;
      m.redraw();
    }
  }

  /**
   * 加载本年度的全部记录（供年度热力图，§19.3）。
   *
   * 数据源与月历/时间线同源（都是 records 资源），只是时间窗不同：
   *   filter[from] = 本年 1 月 1 日；filter[to] = 今天。
   * 单用户一年 ≤ 366 行，一次查询即可拉全（§19.3「一次查询」硬要求），
   * 因此 page[limit] 用 YEAR_FETCH_LIMIT（后端 maxLimit=400）。
   *
   * ★ 为什么 to 取「今天」而非「12 月 31 日」：
   *   未来日期不可能有记录（后端拒绝未来打卡），拉未来区间纯属浪费；
   *   且热力图本身也只需渲染到今天。
   */
  async loadYear() {
    if (!this.currentHabitId) return;
    this.loadingHeatmap = true;
    m.redraw();
    try {
      const from = dayjs().startOf('year').format('YYYY-MM-DD');
      const to = dayjs().format('YYYY-MM-DD');
      const result = await app.store.find('jiedh-checkin-records', {
        filter: {
          habitId: this.currentHabitId,
          from,
          to
        },
        sort: 'checkinDate',
        page: {
          limit: YEAR_FETCH_LIMIT
        }
      });
      const map = {};
      result.forEach(r => {
        map[r.checkinDate()] = r;
      });
      this.yearRecords = map;
    } catch (e) {
      this.error = this.errorMessage(e);
      this.yearRecords = {};
    } finally {
      this.loadingHeatmap = false;
      m.redraw();
    }
  }

  // =========================================================================
  // 业务动作
  // =========================================================================

  /** 切换目标（§18.4）：写入 localStorage 与 URL，重取该目标全部数据。 */
  selectHabit(id) {
    if (this.currentHabitId === id) return;
    this.currentHabitId = id;
    this.page = 1;
    this.activeDate = null;
    window.localStorage.setItem('jiedh-checkin.habit', String(id));

    // ★ 仅在「独立页 /checkin」下同步 URL；个人主页 tab（user.checkin）下不跳转。
    //   个人主页 tab 里若 m.route.set(app.route('checkin')) 会把用户甩到独立页、
    //   丢掉个人主页布局，违背「在个人主页内显示」的诉求；此时只依赖已写入的
    //   localStorage 记忆（移动端切换后无 URL 也能记住）。
    if (String(app.current.get('routeName') ?? '') === 'checkin') {
      m.route.set(app.route('checkin'), {
        habit: id
      });
    }
    Promise.all([this.loadRecords(), this.loadMonth(), this.loadYear()]).then(() => m.redraw());
  }

  /**
   * 今日打卡（独立页走 JSON:API Create，§18.3 ①）。
   *
   * 注意：records 的 Create 端点要求携带 `relationships.habit`，
   * 后端 AbstractDatabaseResource::setValue() 会对 BelongsTo 关系调用 associate()，
   * Service 再据此 findOwnedHabit() 校验归属 —— 缺关系会 404。
   */
  checkin() {
    const habit = this.currentHabit();
    if (!habit || this.busy) return;
    this.busy = true;
    m.redraw();

    // createRecord 只用于「占位新建」，真正的字段/关系在 save() 里随请求体发出
    const record = app.store.createRecord('jiedh-checkin-records');
    record.save({
      status: STATUS_SUCCESS,
      // relationships 里传**模型实例**，Model.save 会转成 {type,id}（Model.getIdentifier）
      relationships: {
        habit
      }
    }).then(() => {
      app.alerts.show({
        type: 'success'
      }, app.translator.trans('jiedh-checkin.forum.toast_success'));
      // 重新拉目标（统计随打卡变化）+ 当月 + 时间线
      return this.refreshAfterWrite();
    }).catch(e => {
      app.alerts.show({
        type: 'error'
      }, this.errorMessage(e));
    }).finally(() => {
      this.busy = false;
      m.redraw();
    });
  }

  /** 撤销某日打卡（等价删除该日记录，§18.5「删除 ≡ 撤销」）。 */
  undoRecord(record) {
    if (this.busy) return;
    this.busy = true;
    m.redraw();
    record.delete().then(() => {
      this.activeDate = null;
      app.alerts.show({
        type: 'success'
      }, app.translator.trans('jiedh-checkin.forum.toast_undo'));
      return this.refreshAfterWrite();
    }).catch(e => {
      app.alerts.show({
        type: 'error'
      }, this.errorMessage(e));
    }).finally(() => {
      this.busy = false;
      m.redraw();
    });
  }

  /** 保存当日详情（心情 / 备注，§18.5「编辑心情 / 备注」）。 */
  saveDetail(record) {
    if (this.busy) return;
    this.busy = true;
    m.redraw();
    record.save({
      mood: this.detailMood > 0 ? this.detailMood : null,
      note: this.detailNote || null
    }).then(() => {
      app.alerts.show({
        type: 'success'
      }, app.translator.trans('jiedh-checkin.forum.toast_saved'));
      // 详情面板内修改不影响统计，只需刷新本地映射与时间线
      this.monthRecords[record.checkinDate()] = record;
      this.activeDate = null;
      return this.loadRecords();
    }).catch(e => {
      app.alerts.show({
        type: 'error'
      }, this.errorMessage(e));
    }).finally(() => {
      this.busy = false;
      m.redraw();
    });
  }

  /** 新建目标（多目标开启时，§18.3 ② / §18.4）。 */
  createHabit() {
    const name = this.newHabitName.trim();
    if (!name || this.busy) return;
    this.busy = true;
    m.redraw();
    const habit = app.store.createRecord('jiedh-checkin-habits');
    habit.save({
      name,
      icon: this.newHabitIcon || null,
      // unit 必填且不可空，缺省由 Service 兜底，但契约上显式给出更稳（后端正则校验）
      unit: 'day'
    }).then(saved => {
      this.showNewHabitForm = false;
      this.newHabitName = '';
      this.newHabitIcon = '';
      app.alerts.show({
        type: 'success'
      }, app.translator.trans('jiedh-checkin.forum.toast_habit_created'));
      // 新建后切到新目标
      this.currentHabitId = Number(saved.id());
      window.localStorage.setItem('jiedh-checkin.habit', String(saved.id()));
      this.page = 1;
      return this.loadHabits();
    }).catch(e => {
      app.alerts.show({
        type: 'error'
      }, this.errorMessage(e));
    }).finally(() => {
      this.busy = false;
      m.redraw();
    });
  }

  /**
   * 导出当前目标的打卡数据（§19.4）。
   *
   * 实现要点：直接命中 forum 栈的 `GET /checkin/export`（登录态 + 仅本人，
   * 后端已把内容协商与文件下载头做好：Content-Type / Content-Disposition /
   * Cache-Control: no-store）。前端不消费响应体，而是用隐藏 `<a>` 触发浏览器
   * 原生下载 —— 这样大数据量走流式传输，无需在前端拼装或占用内存。
   *
   * 为什么不用 app.request：核心的 app.request 会按 JSON 解析响应，
   * 而导出的正文是 CSV/JSON 文本文件，走 `<a download>` 更符合语义也更省事。
   *
   * @param format 导出格式：csv（默认，含 UTF-8 BOM 便于 Excel 打开）或 json
   */
  exportData(format) {
    if (format === void 0) {
      format = 'csv';
    }
    if (!this.currentHabitId) return;
    const base = app.forum.attribute('baseUrl');
    const url = base + '/checkin/export?format=' + encodeURIComponent(format) + '&habitId=' + this.currentHabitId;

    // 隐藏锚点 + download 属性：不新开标签页，直接触发下载
    const a = document.createElement('a');
    a.href = url;
    a.download = ''; // 交给后端 Content-Disposition 决定文件名
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  /**
   * 「分享成就」入口（★ 2026-10-03 戒打卡分享重设计 §4.2.2）。
   *
   * 路由策略（两级降级，绝不白屏 / 不报错）：
   *   ① `shareEnabled === true` 且 `window.jiedhShare?.can('open')` 为真
   *      ⇒ 组装描述符交给 share 的分享面板（海报 / 感言审核 / 渠道 / 保存图片全部复用）；
   *   ② 否则（share 未装 / 未构建 / 被关闭 / 无当前目标）
   *      ⇒ 回退到既有的 `shareAchievement()`（Web Share + 剪贴板），行为与改造前一致。
   *
   * ★ 为什么 `open()` 的 reject 也降级：面板 chunk 动态加载失败（网络 / 产物缺失）时
   *   不能让用户点了没反应，直接退回原生分享是最稳的兜底。
   * ★ 为什么**不隐藏**按钮（§11 Q1 裁决 B）：即使 share 全关，用户仍应有一个可用的分享出口。
   */
  openShare() {
    const config = checkinConfig();
    const api = window.jiedhShare;
    if (config.shareEnabled && api && api.can('open')) {
      const descriptor = this.buildShareDescriptor();
      if (descriptor) {
        // 面板正常关闭时该 Promise 会 resolve（Flarum 的 modal.show 不会因用户取消而 reject），
        // 因此 catch 只会落在「chunk 加载失败 / 面板构造抛错」这类真实异常上。
        Promise.resolve(api.open(descriptor)).catch(e => {
          console.warn('[jiedh-checkin] 分享面板打开失败，已降级为原生分享：', e);
          this.shareAchievement();
        });
        return;
      }
    }
    this.shareAchievement();
  }

  /**
   * 组装 share 面板所需的分享描述符（§4.1.2 契约）。
   *
   * ★ 隐私红线（§6.1）：`text` / `stats` 只承载**聚合统计**，
   *   绝不含逐日打卡明细、备注、心情等敏感内容。
   * ★ `title` 与 `stats.name` 都取目标名：share 侧把它作为海报卡片主标题与面板标题后缀
   *   （见 `text.ts` 的 checkin 分支与 SharePanelModal.title()）。
   *
   * @returns 无当前目标时返回 null（调用方据此回退旧分享）。
   */
  buildShareDescriptor() {
    const habit = this.currentHabit();
    if (!habit) return null;
    const site = app.forum.attribute('title') ?? '';
    const streak = habit.currentStreak();
    const rate = habit.successRate();
    const stats = {
      name: habit.name(),
      // emoji 图标（极短字符），share 侧只做「短文本真值渲染」，空白/过长会自动忽略
      icon: habit.icon() ?? undefined,
      unit: extractText(app.translator.trans('jiedh-checkin.forum.unit_day')),
      currentStreak: streak,
      bestStreak: habit.bestStreak(),
      totalSuccessDays: habit.totalSuccessDays(),
      totalRelapseDays: habit.totalRelapseDays(),
      monthSuccessCount: habit.monthSuccessCount(),
      // 无破戒（successRate 为 null）时按 100% 展示（§18.6 口径，与统计卡一致）；
      // share 的 AchievementStats.successRate 是**百分数**（92 = 92%），故先四舍五入再传。
      successRate: rate === null || rate === undefined ? 100 : Math.round(rate * 100),
      targetDays: habit.targetDays(),
      // ★ 年度热力等级数组（用户需求「把年度打卡放进海报中」，2026-10-03）：
      //   与网页版热力图**共用同一份 yearRecords 与 heatmapLevelForRun() 口径**，
      //   只是压平成「长度 365/366、值 0–4」的纯数组（见 buildYearHeatmapLevels()）。
      //   ★ 数据未加载完时返回全 0 ⇒ share 侧对「全 0」的处理是**整块不绘制**，
      //     而不是画一张空网格，避免分享过早时出现误导性图形。
      //   ★ 隐私：只承载「哪几天连续达标」的聚合等级，不含备注 / 心情等明细。
      heatmap: this.buildYearHeatmapLevels()
    };
    const descriptor = {
      source: 'checkin-achievement',
      title: habit.name(),
      // 复用既有的 `share_text`（站点名 + 连续天数），供复制链接 / 系统分享 / 站外 intent 使用
      text: extractText(app.translator.trans('jiedh-checkin.forum.share_text', {
        site,
        streak
      })),
      stats,
      url: this.checkinShareUrl()
    };

    // P3：**按需**注入「分享到帖子」渠道。
    // ★ 为什么不满足条件时传 `available: false` 而是**整个字段不传**：
    //   §4.5.5「绝不出现假按钮」—— 注入数组为空时 share 的渠道网格里根本没有这一格，
    //   连占位都不会有（`layoutChannels` 只遍历数组，不做补位）。
    const extraChannels = this.buildExtraChannels();
    if (extraChannels.length > 0) {
      descriptor.extraChannels = extraChannels;
      // ★ 用户反馈「没有看到分享到论坛里面啊」的根因是第 13 格被「更多」挡住：
      //   既有 12 渠道已铺满首屏，故必须 prepend，让两个发帖渠道出现在首屏最前几格。
      descriptor.extraChannelsPosition = 'prepend';
    }
    return descriptor;
  }

  /**
   * 组装发帖相关的注入渠道（P3 预填「分享到帖子」+ 2026-10-03 直发「一键发到论坛」，§4.5.1 / §4.8.1）。
   *
   * 三重守卫（§4.5.5 / §4.8.6），任一不满足就**整格隐藏**（返回空数组）：
   *   ① 管理员开关 `sharePostEnabled`（默认关 —— 涉及公开用户数据，必须显式开启）；
   *   ② 已登录（`app.session.user`）；
   *   ③ 有发帖权限（`canStartDiscussion` 能力位）。
   *
   * ★ 关于设计文档 §4.8.6 的「守卫④（标签已配置）」——2026-10-03 已随 D5 改判**取消**：
   *   用户裁决「标签让用户自己选」，故 `share_post_category` 不再是必填硬依赖，
   *   渠道**不因标签未配置而隐藏**；「有没有可用标签」改为点击时的运行时校验
   *   （见 `runDirectPostChannel()`：无可用标签则本地拦截 + 明确提示，绝不发一条没有板块的帖子）。
   *
   * ★ 数组顺序 = 首屏展示顺序（调用方以 `extraChannelsPosition: 'prepend'` 注入）：
   *   把「一键发到论坛」（直发，快捷）排在前面，「分享到帖子」（预填，可编辑）随后。
   *
   * @returns 空数组 = 不注入（share 面板行为与改造前完全一致）。
   */
  buildExtraChannels() {
    if (!checkinConfig().sharePostEnabled) return [];
    if (!this.canStartPost()) return [];
    return [{
      id: 'postDirect',
      // 翻译键由 **share 侧**语言包提供（jiedh-share.forum.channels.postDirect）——
      // 因为渲染发生在 share 面板内，trans 走的是 share 的 translator（§4.6.1 解耦）。
      labelKey: 'postDirect',
      // 核心 Button 的 icon prop：必须是 Font Awesome 类名（渠道格统一用 FA 图标）
      icon: 'fas fa-paper-plane',
      available: true,
      onSelect: context => this.runDirectPostChannel(context)
    }, {
      id: 'post',
      // 翻译键由 **share 侧**语言包提供（jiedh-share.forum.channels.post）——
      // 因为渲染发生在 share 面板内，trans 走的是 share 的 translator（§4.6.1 解耦）。
      labelKey: 'post',
      // 核心 Button 的 icon prop：必须是 Font Awesome 类名（渠道格统一用 FA 图标）
      icon: 'fas fa-comment-dots',
      available: true,
      // share 面板调用时会把「面板当前状态」传进来（renderPoster / approvedRemark）
      onSelect: context => this.runPostChannel(context)
    }];
  }

  /**
   * 是否具备「发起新讨论」的能力（§4.5.5 守卫③）。
   *
   * `canStartDiscussion` 是核心 ForumResource 序列化下来的能力位（vendor/flarum/core
   * src/Api/Resource/ForumResource.php）。★ 用 `!== false` 而不是 `=== true`：
   * 该属性在极端情况（陈旧缓存）下可能缺失，此时**不隐藏**渠道，
   * 让用户点进去由核心发帖链路给权威结论，而不是前端凭猜疑把入口关掉。
   */
  canStartPost() {
    if (!app.session.user) return false;
    return app.forum.attribute('canStartDiscussion') !== false;
  }

  /**
   * 「分享到帖子」渠道的入口（P3，§4.5.2 ①）：**只做隐私确认**，不做别的。
   *
   * ★ 为什么只 `await` 到 `modal.show()` 就返回，而不是等用户点完确认框：
   *   ① share 面板的 `runInjectedChannel()` 会 `await onSelect(...)`，期间把整个渠道网格
   *      置为 busy。若这里一直等到用户做出选择才返回，用户在阅读「打卡统计将公开」这段
   *      时间里，面板上所有格子都会一直转圈，观感很差。
   *   ② 确认框是**栈式**弹出（压在面板之上），用户完全可能点取消 / 点遮罩 / 按 Esc / 点 X
   *      把它关掉。这四条路径都**不会**触发 `onConfirm` ⇒ 天然 fail-safe（不确认 = 不公开），
   *      也就不需要「Promise 永远挂着」这类悬挂处理。
   *
   * ★ `await modal.show(...)` 覆盖的是「确认弹窗 chunk 的动态加载」这段真实耗时，
   *   让按钮在这段时间里保持 loading（用户点了有反馈），而不是完全没有响应。
   *   真正的重活（出图 / 上传 / 预填）全部在 `onConfirm` → `publishPostDraft()` 里。
   */
  async runPostChannel(context) {
    const habit = this.currentHabit();
    if (!habit) return;

    /*
     * 第三参数 `stackModal` 必须显式 `true`：压在 share 面板之上而不是顶掉它 ——
     * 用户点「取消」后应回到分享面板继续操作，而不是被踢回打卡页。
     */
    await app.modal.show(() => import(/* webpackChunkName: 'forum/components/CheckinPostConfirmModal', webpackMode: 'lazy-once' */ './CheckinPostConfirmModal'), {
      onConfirm: () => {
        // 不 await：本回调由确认弹窗的退场回调触发，同步返回即可（流程自行跑完）
        void this.publishPostDraft(habit, context);
      }
    }, true);
  }

  /**
   * 隐私确认通过后的**完整发布准备流程**（§4.5.2 ②~④）。
   *
   * ① 关闭整个弹窗栈（分享面板 + 上层的隐私确认层）—— 由本方法负责收尾，
   *    确认弹窗只做「退场动画 + 同步回调」，不依赖动画事件（详见下方步骤 ① 的注释）
   * ② 出图 + 上传：海报是**加分项**，任一环节失败都只降级为「仅发文字」并提示
   * ③ 组装标题 / 正文（感言只取**已过审**的定稿）→ 预填原生发帖框
   * ④ 用户**亲手点「发布」**，走标准发帖链路
   *    （标题 / 正文 / 配图三处既有审核自动生效，§4.5.4 ⇒ 全程零新增审核代码）
   *
   * ★ 全程不新增任何服务端代码：落点就是核心的 `POST /api/discussions`。
   *
   * @param habit   当前目标（标题与统计的来源）
   * @param context share 面板交出的运行时上下文（出图函数 + 已过审感言）
   */
  async publishPostDraft(habit, context) {
    /*
     * ① 先关掉**整个弹窗栈**（分享面板 + 上层的隐私确认层）：
     *    · 随后要打开的原生发帖框是**全局浮层**（`app.composer`，与 `app.modal` 是两套体系），
     *      若把弹窗留在下层会互相抢焦点；
     *    · 面板压入的那条 history 记录若不清掉，会一直残留在返回键栈里
     *      （面板 `onremove()` 会调用 `releaseHistoryGuard()` 顺手消费掉）。
     *
     *    ★ 为什么是**循环** `close()` 而不是一次：本方法由确认弹窗的回调**同步**触发
     *      （见 `CheckinPostConfirmModal.confirm()`：`this.hide()` 之后立刻 `onConfirm()`），
     *      此刻确认层**仍在栈顶**，单次 `close()` 只会弹掉它、把分享面板留在下层。
     *      循环到栈空语义才正确，且**不依赖任何动画事件**（避免流程被动画事件卡死）。
     *    ★ `ModalManagerState.close()` 对空栈有 `if (!this.modal) return` 守卫，循环安全。
     */
    while (app.modal.isModalOpen()) app.modal.close();

    /*
     * ★ 2026-10-03 用户反馈：「分享弹窗消失后，页面没有任何变化。用户都不知道发生什么了？
     *   有没有分享成功」—— 弹窗一关，下面 ②③④ 三段才是真正的耗时（开发环境要十几秒），
     *   所以**清栈之后立刻**挂上「正在分享中」浮层，并统一收在 `finally`（任何退出路径都收起）。
     */
    showPublishProgress('share_progress_poster');
    try {
      // ② 出图 + 上传：海报是加分项，任一环节失败都只降级、不阻断（§4.5.2 ③）
      let posterBbcode = '';
      if (context?.renderPoster) {
        try {
          const blob = await context.renderPoster();
          if (blob) {
            // 出图完成 ⇒ 进入「上传海报」阶段（这条链路里最慢的一段，必须单独提示）
            setPublishProgress('share_progress_uploading');
            posterBbcode = await this.uploadPoster(blob);
          }
        } catch (error) {
          console.warn('[jiedh-checkin] 分享到帖子：海报出图 / 上传失败，已降级为纯文字：', error);
        }
      }
      if (!posterBbcode) {
        // 明确提示用户「图没带上」，避免他以为海报也发出去了（§4.5.3）
        // ★ 浮层层级(1055)低于 alerts(1060)，所以这条 warning 在转圈期间照样看得见。
        app.alerts.show({
          type: 'warning'
        }, app.translator.trans('jiedh-checkin.forum.share_poster_failed'));
      }

      // ③ 组装标题 / 正文（感言只取已过审的定稿，未过审一律不进正文 —— 红线③）
      // ★ 2026-10-03 用户裁决：标题太长，缩短为「我已经成功坚持戒N天！戒友们一起加油！」，
      //   只保留 `streak` 一个变量；站点名与目标名都不再进标题（目标名改由海报呈现）。
      setPublishProgress('share_progress_composer');
      const title = extractText(app.translator.trans('jiedh-checkin.forum.share_post_title', {
        streak: habit.currentStreak()
      }));
      const body = this.buildPostBody(habit, context?.approvedRemark, posterBbcode);

      // ④ 预填发帖框；这一步失败必须给用户明确提示（绝不静默吞掉）
      try {
        await this.openPostComposer(title, body);
      } catch (error) {
        console.warn('[jiedh-checkin] 分享到帖子：发帖框打开失败：', error);
        app.alerts.show({
          type: 'error'
        }, app.translator.trans('jiedh-checkin.forum.share_failed'));
      }
    } finally {
      // 浮层是**阻断式**的（盖住整页）⇒ 成功、降级、异常任何一条路径都必须收起
      hidePublishProgress();
    }
  }

  /* ═══════════════════════════════════════════════════════════════════════
   * 「一键发到论坛」直发渠道（2026-10-03 §4.8；D5 已改判为「用户自选板块」）
   * ═══════════════════════════════════════════════════════════════════════
   * 与上面 `runPostChannel`（预填）**并列不替换**：预填服务于「想先编辑再发」的用户，
   * 直发服务于「只想快点发出去」的用户。两者共用同一份文案（share_post_title /
   * buildPostBody）与同一套海报链路（context.renderPoster / uploadPoster）。
   *
   * ★ 落点 = 核心既有端点 `POST /api/discussions`：**零服务端代码、零审核代码**，
   *   标题 / 正文 / 配图三处审核都在事件层自动生效（§4.8.7）。
   */

  /**
   * 加载并返回「当前用户有权在其下发起新讨论」的标签列表（§4.8.3 ②）。
   *
   * 为什么由调用方（而非弹窗）加载：
   *   · 弹窗保持「极薄纯展示层」，可独立成 chunk，不给 `/checkin` 首屏加体积；
   *   · 「有没有可用板块」属业务判断，放这里便于在 show() 之前拦下异常。
   *
   * @returns 可发帖的标签（已排序）；无可用标签时为空数组（调用方据此本地拦截）。
   */
  async selectableTags() {
    // ① 触发加载：`app.tagList` 由 flarum/tags 注入（与核心 TagSelectionModal 用的是同一入口）。
    //    未装该扩展时为 undefined ⇒ 跳过加载，下一步 store 里自然也没有标签 ⇒ 返回空数组。
    const tagList = app.tagList;
    if (tagList && typeof tagList.load === 'function') {
      try {
        await tagList.load(['parent']);
      } catch (error) {
        console.warn('[jiedh-checkin] 一键发到论坛：标签列表加载失败（将按无可用板块处理）：', error);
      }
    }

    // ② 只保留「有权在此发起新讨论」的标签（canStartDiscussion 由 flarum/tags 序列化下发）。
    //    ★ 用严格 `=== true`：拿不到该能力位时不冒险显示，避免用户点了必 403（= 假按钮）。
    const tags = app.store.all('tags').filter(tag => typeof tag.canStartDiscussion === 'function' && tag.canStartDiscussion() === true);

    // ③ 排序：优先按 position（与站点标签顺序一致），position 为空（子标签）的排到后面按名称排。
    return tags.sort((a, b) => {
      const av = typeof a.position === 'function' ? a.position() : null;
      const bv = typeof b.position === 'function' ? b.position() : null;
      const an = av === null || av === undefined ? Number.MAX_SAFE_INTEGER : Number(av);
      const bn = bv === null || bv === undefined ? Number.MAX_SAFE_INTEGER : Number(bv);
      if (an !== bn) return an - bn;
      return String(a.name() ?? '').localeCompare(String(b.name() ?? ''));
    });
  }

  /**
   * 「一键发到论坛」渠道的入口（§4.8.3 ①~②）。
   *
   * ★ 为什么只 `await` 到 `modal.show()` 就返回（与 `runPostChannel` 同一理由）：
   *   share 面板的 `runInjectedChannel()` 会 `await onSelect(...)` 并在此期间把整个渠道网格置 busy。
   *   若这里一直等到用户选完板块、点完确认才返回，用户阅读确认文案的整段时间里所有格子都在转圈，
   *   观感很差。真正的重活（出图 / 上传 / 建帖 / 跳转）全在 `onConfirm` → `publishDirectPost()` 里。
   *   而 `await modal.show(...)` 覆盖的是「弹窗 chunk 动态加载」这段真实耗时，让按钮有反馈。
   *
   * ★ 标签列表必须在 show() 之前拿到：拿不到就没有可选项，此时**本地拦截 + 明确提示**，
   *   绝不弹一个「选了也发不出去」的空弹窗（§4.8.6 守卫④的精神）。
   */
  async runDirectPostChannel(context) {
    const habit = this.currentHabit();
    if (!habit) return;

    // ① 运行时校验：有没有可用板块
    const tags = await this.selectableTags();
    if (tags.length === 0) {
      app.alerts.show({
        type: 'error'
      }, app.translator.trans('jiedh-checkin.forum.share_direct_no_category'));
      return;
    }

    // ② 确认弹窗（含用户自选板块）。
    //    第三参数 `stackModal` 必须显式 `true`：压在 share 面板之上而不是顶掉它 ——
    //    用户点「取消」后应回到分享面板继续操作，而不是被踢回打卡页。
    await app.modal.show(() => import(/* webpackChunkName: 'forum/components/CheckinDirectPostModal', webpackMode: 'lazy-once' */ './CheckinDirectPostModal'), {
      tags,
      // 默认预选来自后台设置（0 = 不预选）；弹窗会校验该 id 确实在可选项里，脏数据不会被强行预选
      defaultTagId: checkinConfig().sharePostCategory,
      onConfirm: tag => {
        // 不 await：本回调由确认弹窗的退场回调触发，同步返回即可（流程自行跑完）
        void this.publishDirectPost(habit, tag, context);
      }
    }, true);
  }

  /**
   * 组装要提交的标签 id 列表（§4.8.4）。
   *
   * ★ 为什么子标签必须连同父标签一起提交：
   *   flarum/tags 的 `min_primary_tags` 默认 1，而「主标签」= 无父级的顶层标签。
   *   若用户只选了「戒色-文章」这类子标签，主标签数为 0 ⇒ 直接 403/422。
   *   与发帖框内部的 `requireParentTag` 是同一口径，这里手工补齐。
   */
  resolveTagIds(tag) {
    const ids = [];
    const parent = typeof tag.parent === 'function' ? tag.parent() : null;
    if (parent && typeof parent.id === 'function') ids.push(Number(parent.id()));
    ids.push(Number(tag.id()));

    // 去重 + 过滤非法值（防脏数据）
    return ids.filter((id, index) => Number.isFinite(id) && id > 0 && ids.indexOf(id) === index);
  }

  /**
   * 调用核心端点创建讨论（§4.8.4）。
   *
   * ★ 请求体是标准 JSON:API：`attributes.title` + `attributes.content` + `relationships.tags`。
   *   `app.request` 默认按 JSON 序列化 body，**不需要**像上传那样显式 `serialize`。
   * ★ 标签 id 必须转**字符串**（JSON:API 约定）。
   * ★ 频控（同用户 10 秒内重复发帖 ⇒ 429）与审核（422）都由核心的默认错误处理器
   *   给出本地化提示，因此这里**不传自定义 errorHandler**（见 publishDirectPost 的 catch）。
   *
   * @param params 标题 / 正文 / 标签 id 数组
   * @returns 原始 JSON:API 文档（含 `data.id` / `data.attributes.slug`）
   */
  async createDiscussion(params) {
    const apiUrl = String(app.forum.attribute('apiUrl') ?? '');
    return app.request({
      method: 'POST',
      url: apiUrl + '/discussions',
      body: {
        data: {
          type: 'discussions',
          attributes: {
            title: params.title,
            content: params.content
          },
          relationships: {
            tags: {
              data: params.tagIds.map(id => ({
                type: 'tags',
                id: String(id)
              }))
            }
          }
        }
      }
    });
  }

  /**
   * 确认通过后的**完整直发流程**（§4.8.3 ①~⑦）。
   *
   * ① 关闭整个弹窗栈（分享面板 + 上层的板块确认层）—— 由本方法负责收尾，
   *    确认弹窗只做「退场动画 + 同步回调」，不依赖动画事件（详见下方步骤 ① 的注释）
   * ② 出图 + 上传：海报是**加分项**，任一环节失败都只降级为「仅发文字」并提示
   * ③ 复用预填渠道的同一份文案（`share_post_title` + `buildPostBody`），避免两套措辞漂移
   * ④ 直调 `POST /api/discussions` 创建讨论（审核在事件层自动生效）
   * ⑤ 成功 → 入 store → 成功提示 → 跳转到新讨论
   *
   * ★ 失败时**不关闭**面板（在步骤 ① 之后面板已被关闭，但失败只弹提示、不改动其它状态）：
   *   用户可重新打开分享面板、换个渠道或稍后重试；绝不留下「半成品草稿」。
   *
   * @param habit   当前目标（标题与统计的来源）
   * @param tag     用户在确认弹窗里选中的标签
   * @param context share 面板交出的运行时上下文（出图函数 + 已过审感言）
   */
  async publishDirectPost(habit, tag, context) {
    /*
     * ① 先关掉**整个弹窗栈**（分享面板 + 上层的板块确认层），理由与 `publishPostDraft` 一致：
     *    避免两个浮层互相抢焦点、并顺手消费掉面板压入的那条 history 记录。
     *
     *    ★ 为什么是**循环** `close()` 而不是一次：本方法由确认弹窗的回调**同步**触发
     *      （见 `CheckinDirectPostModal.confirm()`：`this.hide()` 之后立刻 `onConfirm(tag)`），
     *      此刻确认层**仍在栈顶**，单次 `close()` 只会弹掉它、把分享面板留在下层。
     *      循环到栈空语义才正确，且**不依赖任何动画事件**（避免流程被动画事件卡死）。
     *    ★ `ModalManagerState.close()` 对空栈有 `if (!this.modal) return` 守卫，循环安全。
     */
    while (app.modal.isModalOpen()) app.modal.close();

    // ② 出图 + 上传：海报是加分项，任一环节失败都只降级、不阻断（§4.8.5）
    // ★ 2026-10-03 用户反馈：清栈之后才是真正耗时的三段（出图 → 上传 → 建帖），
    //   开发环境要十几秒，期间「看起来页面什么都没发生」⇒ 立刻挂上进度浮层，
    //   并统一在 `finally` 收起（与 `publishPostDraft` 完全同一套口径）。
    showPublishProgress('share_progress_poster');
    try {
      let posterBbcode = '';
      if (context?.renderPoster) {
        try {
          const blob = await context.renderPoster();
          if (blob) {
            // 出图完成 ⇒ 进入「上传海报」阶段（这条链路里最慢的一段，必须单独提示）
            setPublishProgress('share_progress_uploading');
            posterBbcode = await this.uploadPoster(blob);
          }
        } catch (error) {
          console.warn('[jiedh-checkin] 一键发到论坛：海报出图 / 上传失败，已降级为纯文字：', error);
        }
      }
      if (!posterBbcode) {
        // 明确提示用户「图没带上」，避免他以为海报也发出去了
        // ★ 浮层层级(1055)低于 alerts(1060)，所以这条 warning 在转圈期间照样看得见。
        app.alerts.show({
          type: 'warning'
        }, app.translator.trans('jiedh-checkin.forum.share_poster_failed'));
      }

      // ③ 文案与正文：**完全复用**预填渠道的同一份口径（单一真值源）
      // ★ 2026-10-03 用户裁决：标题只保留 `streak`（与 `runPostChannel` 完全一致，勿改一处漏一处）。
      setPublishProgress('share_progress_publishing');
      const title = extractText(app.translator.trans('jiedh-checkin.forum.share_post_title', {
        streak: habit.currentStreak()
      }));
      const content = this.buildPostBody(habit, context?.approvedRemark, posterBbcode);

      // ④ 直接创建讨论
      try {
        const response = await this.createDiscussion({
          title,
          content,
          tagIds: this.resolveTagIds(tag)
        });

        /*
         * ⑤ 成功：**先收起浮层**，再并入 store → 成功提示 → 跳转到新讨论。
         *   alerts 的层级虽然高于浮层（1060 > 1055），但「转圈还在转、同时提示已发布」
         *   会让人以为仍在处理、反而更困惑；这里显式先 hide，把「处理中 → 已完成」
         *   切成清清楚楚的两拍。下面的 `finally` 只是兜底（hide 是幂等的）。
         */
        hidePublishProgress();
        if (response && typeof response === 'object') app.store.pushPayload(response);
        const discussion = response?.data?.id ? app.store.getById('discussions', response.data.id) : null;
        app.alerts.show({
          type: 'success'
        }, app.translator.trans('jiedh-checkin.forum.share_direct_success'));
        if (discussion) m.route.set(app.route.discussion(discussion));
      } catch (error) {
        console.warn('[jiedh-checkin] 一键发到论坛：创建讨论失败：', error);
        // 核心 `app.request` 的默认错误处理器已按状态码弹出本地化提示（429 频控 / 422 审核 /
        // 403 权限 / 0 网络…）；只有异常没带 `alert`（非 RequestError）时才补一句兜底提示，
        // 避免出现「双重弹窗」。
        if (!error || !error.alert) {
          app.alerts.show({
            type: 'error'
          }, app.translator.trans('jiedh-checkin.forum.share_direct_failed'));
        }
      }
    } finally {
      // 浮层是**阻断式**的（盖住整页）⇒ 成功、降级、异常任何一条路径都必须收起
      hidePublishProgress();
    }
  }

  /**
   * 把海报 PNG 上传到 fof/upload，换回可直接插进正文的 BBCode（§4.5.2 ③）。
   *
   * ── 为什么复用 fof/upload 而不是自建上传接口 ──────────────────────────────
   *   ① 上传链路（存储适配器 / 体积与类型限制 / SVG 净化）全部沿用站点既有配置，零新增代码；
   *   ② **配图审核自动生效**：content-review 的 `InterceptPostImageUpload` 挂在
   *      `FoF\Upload\Events\File\WillBeUploaded` 上（临时文件已就绪、**落盘之前**），
   *      任何走 fof/upload 的图片都会被送审，违规直接 422 中断且不产生孤儿附件
   *      ⇒ 这里不需要、也不应该再写一遍审核逻辑（§4.5.4）。
   *
   * ── 契约（已核对 vendor/fof/upload，勿凭记忆改）─────────────────────────
   *   · 端点：`POST {apiUrl}/fof/upload`
   *   · 字段：`files[]`（处理器读 `Arr::get($request->getUploadedFiles(), 'files', [])`）
   *   · 请求体必须原样透传 FormData ⇒ 必须显式 `serialize: (raw) => raw`，
   *     否则核心 `app.request` 会把它 JSON 序列化成 `{}`
   *   · 响应：JSON:API `{ data: [{ attributes: { bbcode, url, ... } }] }`
   *     （`vendor/fof/upload/src/Api/Resource/FileResource.php` 暴露 `bbcode`）
   *
   * ★ 失败一律**吞掉并返回空串**（调用方据此降级为「仅发文字」并提示），
   *   绝不把异常抛进用户流程 —— 海报只是加分项，不能因为一张图阻断发帖。
   *
   * @param  blob 海报 PNG
   * @return 可直接插入编辑器的 BBCode；失败时为空串
   */
  async uploadPoster(blob) {
    try {
      const body = new FormData();
      // ★ 字段名必须带方括号（`files[]`），这是 fof/upload 的硬契约，写成 `files` 会 422
      body.append('files[]', new File([blob], 'checkin-achievement.png', {
        type: 'image/png'
      }));
      const response = await app.request({
        method: 'POST',
        url: String(app.forum.attribute('apiUrl') ?? '') + '/fof/upload',
        // ★ 必须原样透传 FormData（核心默认按 JSON 序列化）
        serialize: raw => raw,
        body
      });
      const bbcode = response?.data?.[0]?.attributes?.bbcode;
      if (typeof bbcode === 'string' && bbcode !== '') return bbcode;
      console.warn('[jiedh-checkin] 分享到帖子：上传成功但响应里没有 bbcode', response);
      return '';
    } catch (error) {
      console.warn('[jiedh-checkin] 分享到帖子：海报上传失败（将降级为纯文字）：', error);
      return '';
    }
  }

  /**
   * 组装「分享到帖子」的正文（§4.5.3 预填内容规范）。
   *
   * 正文结构（每段之间空一行，便于用户在编辑框里继续补充）：
   *
   *   成就概览：连续 N 天 · 最长 M 天 · 累计 X 天 · 成功率 Y%
   *   （仅当感言已过审）用户感言
   *   #话题标签
   *   海报 BBCode
   *
   * ★ 隐私红线（§6.1 / §4.5.3）：正文**只含聚合统计**，
   *   绝不含逐日打卡明细、备注、心情；感言仅在 `approvedRemark` 有值（= 已过实时审核）
   *   时才写入 —— 未填写 / 送审中 / 未通过一律不含感言。
   * ★ 为什么标签原样透传：`sharePostTags` 是管理员配置的**原文**（如「#戒打卡 #坚持」），
   *   本站不负责解析或校验话题语法，交给核心的发帖链路处理。
   *
   * @param habit          当前目标（统计来源）
   * @param approvedRemark 面板内**已过审**的感言定稿；未填写 / 未过审时为 undefined
   * @param posterBbcode   fof/upload 返回的海报 BBCode；空串表示无图
   */
  buildPostBody(habit, approvedRemark, posterBbcode) {
    const unit = extractText(app.translator.trans('jiedh-checkin.forum.unit_day'));
    const rate = habit.successRate();

    // 成就概览：全部是聚合数字，无任何逐日明细（红线④）
    const overview = extractText(app.translator.trans('jiedh-checkin.forum.share_post_overview', {
      current: habit.currentStreak(),
      best: habit.bestStreak(),
      total: habit.totalSuccessDays(),
      // 无破戒（successRate 为 null）时按 100% 展示，口径与统计卡、成就卡一致
      rate: rate === null || rate === undefined ? 100 : Math.round(rate * 100),
      unit
    }));
    const lines = [overview];

    // 感言：**只**取已过审的定稿（`approvedRemark` 由 share 面板在审核通过后才赋值）
    const remark = String(approvedRemark ?? '').trim();
    if (remark !== '') lines.push('', remark);

    // 话题标签：管理员配置原文，留空则整行不出现
    const tags = String(checkinConfig().sharePostTags ?? '').trim();
    if (tags !== '') lines.push('', tags);
    if (posterBbcode !== '') lines.push('', posterBbcode);
    return lines.join('\n');
  }

  /**
   * 预填并展开核心的原生发帖框（§4.5.6「预填的时序注意」，三个踩坑点都在这里收敛）。
   *
   * 时序（★ 顺序不可调换，每一步都对着核心源码核过）：
   *   ① `app.composer.load(factory, { user })` —— 把一个「返回 Promise 的工厂函数」交给核心，
   *      核心会 `(await factory()).default` 拿到组件类（ComposerState.load()）。
   *      ★ 这里必须用**动态 import 核心惰性 chunk** 的写法（本行必须整行写完、不能换行）：
   *      js/ 的 autoChunkNameLoader 会按正则逐行把 `flarum/` 前缀的异步导入改写成
   *      `flarum.reg.asyncModuleImport(...)`，这是扩展侧加载核心惰性 chunk 的唯一正确姿势
   *      （先例：packages/share/js/src/forum/index.tsx 打开 SignUpModal；
   *       核心自用先例：vendor/flarum/core/js/src/forum/components/IndexSidebar.tsx 开新讨论）。
   *   ② `await app.composer.show()` —— 挂载后才创建 `fields.title`（DiscussionComposer.oninit）
   *      与文本编辑器，所以**标题不能**在 show() 之前设置（会被 oninit 覆盖掉）。
   *   ③ `app.composer.fields.title(title)` —— 写入标题。
   *   ④ `await app.composer.editorReady()` —— 等编辑器构建完成
   *      （Composer 的 onTextEditorBuilt 会 resolve 这个 Promise），此时才能插入正文。
   *   ⑤ `editor.insertAtCursor(body, false)` —— 第二参数 `false` = **不做二次转义**，
   *      让 BBCode / 话题标签按原文插入编辑器。
   *
   * ★ 刻意**不**跳转路由：发帖框是全局浮层，在 `/checkin` 上一样能正常展开与提交；
   *   发布成功后核心 DiscussionComposer 自己会 `m.route.set` 到新讨论页。
   *   若在这里额外 `m.route.set`，反而会让浮层随页面组件重建而闪一下。
   *
   * @param title 帖子标题（纯文本）
   * @param body  帖子正文（含过审感言 + 标签 + 海报 BBCode）
   */
  async openPostComposer(title, body) {
    // ① 载入原生讨论发帖组件（惰性，走核心 chunk；本行必须整行写完，原因见方法注释 ①）
    await app.composer.load(() => flarum.reg.asyncModuleImport('flarum/forum/components/DiscussionComposer'), {
      user: app.session.user
    });

    // ② 展开浮层（挂载后才会有 fields.title 与 editor）
    await app.composer.show();

    // ③ 标题
    app.composer.fields.title(title);

    // ③′ 管理员预设的分类 / 标签（同样必须在 show() 之后写入，原因与 title 相同）
    await this.applyPresetTags();

    // ④ 等编辑器就绪，再插入正文
    await app.composer.editorReady();
    app.composer.editor?.insertAtCursor(body, false);
  }

  /**
   * 应用管理员预设的分类 / 标签（设置项 `sharePostCategory`，§4.7）。
   *
   * ★ 为什么必须在 `show()` **之后**写入：`ComposerState.load()` 在浮层已打开时会先
   *   `clear()` 重建 `fields`（只剩 content），随后组件 oninit 才补齐自己的字段。
   *   所以预设标签要等挂载完成、字段稳定后再赋值（与 title 同一个原因，§4.5.6）。
   * ★ 与 flarum/tags 的约定（已核对 `vendor/flarum/tags/js/src/forum/addTagComposer.js`）：
   *   预设标签 = `app.composer.fields.tags` 放一个 Tag **模型数组**；
   *   提交时该扩展会把 `fields.tags` 写进 `data.relationships.tags`。
   * ★ 失败一律**静默**：预设标签只是便利项，取不到就让用户自己在发帖框里选。
   */
  async applyPresetTags() {
    const tagId = checkinConfig().sharePostCategory;
    // 0（或非法值）表示「不预选」，由用户在发帖框里手动选择
    if (!tagId || tagId <= 0) return;
    try {
      const tag = await app.store.find('tags', tagId);
      if (tag) app.composer.fields.tags = [tag];
    } catch (error) {
      // flarum/tags 未启用 / 该 id 不存在 / 网络异常：都只是拿不到预设，不影响发帖
      console.warn('[jiedh-checkin] 分享到帖子：预设标签加载失败（已忽略）：', error);
    }
  }

  /**
   * 拼「打卡独立页 + 归因参数」的绝对链接（§6.2.3：分享链接必须是绝对 URL）。
   *
   * 口径与 `utils/text.ts` 的 `withRef()` 一致：`?ref=share` 幂等，
   * 即使 share 侧再追加一次也不会出现 `ref=share&ref=share`。
   */
  checkinShareUrl() {
    const base = String(app.forum.attribute('baseUrl') ?? '').trim();
    const rel = String(app.route('checkin') ?? '');
    let abs = rel;
    if (base) {
      try {
        abs = new URL(rel, base).toString();
      } catch {
        // baseUrl 非法（理论上不会发生）→ 退回相对链接，宁可降级也不抛错
        abs = rel;
      }
    }
    return abs + (abs.indexOf('?') >= 0 ? '&' : '?') + 'ref=share';
  }

  /**
   * 分享当前成就 —— **降级路径**（§20.1 / §4.2.2 ②）。
   *
   * ★ 定位变化（2026-10-03 戒打卡分享重设计）：本方法原先承担全部分享职责，
   *   现已退居为「share 扩展不可用 / 被关闭 / 面板加载失败」时的兜底方案。
   *   主路径改为 `openShare()` → `window.jiedhShare.open(descriptor)`（复用 share 面板）。
   *
   * 降级方案本身保持**自包含、零新依赖、隐私安全**：
   *   - 优先调用浏览器原生 Web Share API（移动端可唤起系统分享面板，体验最佳）；
   *   - 不可用时回退为「复制文本到剪贴板」。
   *   分享文本**只含站点名与连续天数**，绝不含备注/心情等敏感内容（§20.1 隐私要求）。
   *
   * 该方法和 `copyFallback()` **保留不删**（红线：可降级）。
   */
  shareAchievement() {
    const habit = this.currentHabit();
    if (!habit) return;
    const site = app.forum.attribute('title') ?? '';
    const streak = habit.currentStreak();
    const text = extractText(app.translator.trans('jiedh-checkin.forum.share_text', {
      site,
      streak
    }));
    const done = () => app.alerts.show({
      type: 'success'
    }, app.translator.trans('jiedh-checkin.forum.share_copied'));
    const fail = () => app.alerts.show({
      type: 'error'
    }, app.translator.trans('jiedh-checkin.forum.share_failed'));

    // ① 原生 Web Share API（需安全上下文 https/localhost，移动端普遍可用）
    if (typeof navigator.share === 'function') {
      navigator.share({
        title: site,
        text
      }).then(done).catch(e => {
        // 用户主动取消（AbortError）不算失败，静默忽略；其余错误回退到复制
        if (e && e.name === 'AbortError') return;
        this.copyFallback(text) ? done() : fail();
      });
      return;
    }

    // ② 回退：复制到剪贴板
    this.copyFallback(text) ? done() : fail();
  }

  /**
   * 复制文本到剪贴板的兼容实现（§20.1 回退路径）。
   *
   * 优先用异步剪贴板 API（需安全上下文），不可用时用经典的
   * 「临时 textarea + execCommand('copy')」兜底（兼容 http 环境与旧浏览器）。
   *
   * @returns 是否复制成功
   */
  copyFallback(text) {
    // ① 现代 API（navigator.clipboard 仅在 https/localhost 下存在）
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      // 注意：writeText 是异步的，这里无法同步拿到结果；乐观地返回 true，
      // 真正的失败会由调用方的 catch 分支处理。
      navigator.clipboard.writeText(text).catch(() => {
        /* 失败时静默，下面不会再重试（避免弹两次提示） */
      });
      return true;
    }

    // ② 经典兜底：临时 textarea + execCommand
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }

  /** 翻月（±1 或 0 = 回本月，§18.2 交互）。 */
  goMonth(deltaMonths) {
    this.monthCursor = deltaMonths === 0 ? dayjs().startOf('month') : this.monthCursor.add(deltaMonths, 'month').startOf('month');
    this.activeDate = null;
    this.loadMonth().then(() => m.redraw());
  }

  /** 时间线翻页（§18.5）。 */
  goToPage(page) {
    this.page = page;
    this.loadRecords().then(() => m.redraw());
  }

  /**
   * 写操作后统一刷新：目标（统计只读属性随之后端重算）+ 当月 + 时间线。
   * 之所以要重取目标：统计卡（currentStreak 等）是 habits 资源的只读聚合属性。
   */
  async refreshAfterWrite() {
    try {
      const all = await app.store.find('jiedh-checkin-habits');
      this.habits = all.filter(h => !h.isArchived());
    } catch {
      // 统计刷新失败不阻断主流程（记录本身已写入成功）
    }
    await Promise.all([this.loadRecords(), this.loadMonth(), this.loadYear()]);
  }

  // =========================================================================
  // 渲染
  // =========================================================================

  view() {
    // 游客：给出登录引导（§9.3 游客态）。
    // 外壳（独立页的 .CheckinPage + .container、或个人主页的 UserPage 布局）由各自的
    // Page/UserPage 负责，本组件只产出内容本身。
    if (!app.session.user) {
      return m(Placeholder, {
        text: app.translator.trans('jiedh-checkin.forum.guest_tip')
      });
    }
    return m("div", {
      className: "CheckinContent"
    }, this.renderHeader(), this.error && m("div", {
      className: "CheckinError"
    }, this.error), this.loadingHabits ? m(LoadingIndicator, null) : this.renderBody());
  }

  /** 头部：标题 + 目标切换器 + 新建目标 + 设置入口。 */
  renderHeader() {
    const config = checkinConfig();
    // 多目标开关（§18.4）：关时隐藏「新建目标」与切换器，后端以 403 兜底
    const multi = config.multiHabit;
    // 上限内才允许新建（前端体验层；后端 CheckinHabitResource 会以 max_habits 校验）
    const canCreateHabit = this.habits.length < config.maxHabits;
    return m("header", {
      className: "CheckinHeader"
    }, m("div", {
      className: "CheckinHeader-title"
    }, m("h1", null, app.translator.trans('jiedh-checkin.forum.page_title')), this.currentHabit() && m("span", {
      className: "CheckinHeader-sub"
    }, this.renderHabitLabel(this.currentHabit()))), m("div", {
      className: "CheckinHeader-actions"
    }, multi && this.habits.length > 1 && this.renderHabitSwitcher(), multi && canCreateHabit && m(Button, {
      className: "Button Button--primary",
      icon: "fas fa-plus",
      onclick: () => this.showNewHabitForm = !this.showNewHabitForm
    }, app.translator.trans('jiedh-checkin.forum.action_new_habit')), app.session.user?.isAdmin?.() && this.renderAdminLink()));
  }

  /** 目标切换器：横向可滚动的 chip 列表（移动端友好）。 */
  renderHabitSwitcher() {
    return m("div", {
      className: "CheckinSwitcher",
      role: "tablist"
    }, this.habits.map(h => {
      const active = Number(h.id()) === this.currentHabitId;
      return m("button", {
        key: h.id(),
        type: "button",
        role: "tab",
        "aria-selected": active,
        className: 'CheckinSwitcher-item' + (active ? ' is-active' : ''),
        onclick: () => this.selectHabit(Number(h.id()))
      }, h.icon() && m("span", {
        className: "CheckinSwitcher-icon"
      }, h.icon()), m("span", {
        className: "CheckinSwitcher-name"
      }, h.name()));
    }));
  }

  /** 后台设置入口（管理员）。 */
  renderAdminLink() {
    const adminUrl = app.forum.attribute('adminUrl');
    if (!adminUrl) return null;
    return m("a", {
      className: "CheckinHeader-settings Button Button--link",
      href: adminUrl + '#/jiedh-checkin'
    }, m(Icon, {
      name: "fas fa-cog"
    }), " ", app.translator.trans('jiedh-checkin.forum.action_settings'));
  }

  /** 页面主体：无目标 → 空态；有目标 → 统计卡 + 月历 + 时间线。 */
  renderBody() {
    // 无目标且多目标关闭：给出空态引导（可重新触发默认目标创建）
    if (this.habits.length === 0 || !this.currentHabit()) {
      return m("div", {
        className: "CheckinEmpty"
      }, m(Placeholder, {
        text: app.translator.trans('jiedh-checkin.forum.empty_tip')
      }), m("div", {
        className: "CheckinEmpty-action"
      }, m(Button, {
        className: "Button Button--primary",
        icon: "fas fa-flag-checkered",
        loading: this.busy,
        onclick: () => this.loadHabits()
      }, app.translator.trans('jiedh-checkin.forum.action_start'))));
    }
    const habit = this.currentHabit();
    return m("div", {
      className: "CheckinBody"
    }, this.showNewHabitForm && this.renderNewHabitForm(), this.renderStatsCards(habit), this.renderActionsBar(), this.renderHeatmapCard(), this.renderCalendarCard(), this.activeDate && this.renderDayDetail(), this.renderTimelineCard());
  }

  /** 新建目标内联表单（名称 + emoji 图标）。 */
  renderNewHabitForm() {
    return m("div", {
      className: "CheckinNewHabit Card"
    }, m("div", {
      className: "Card-body"
    }, m("label", {
      className: "CheckinNewHabit-field"
    }, m("span", null, app.translator.trans('jiedh-checkin.forum.new_habit_name')), m("input", {
      className: "FormControl",
      type: "text",
      maxlength: 50,
      value: this.newHabitName,
      oninput: e => this.newHabitName = e.target.value,
      placeholder: app.translator.trans('jiedh-checkin.forum.new_habit_name_placeholder')
    })), m("label", {
      className: "CheckinNewHabit-field"
    }, m("span", null, app.translator.trans('jiedh-checkin.forum.new_habit_icon')), m("input", {
      className: "FormControl CheckinNewHabit-icon",
      type: "text",
      maxlength: 4,
      value: this.newHabitIcon,
      oninput: e => this.newHabitIcon = e.target.value,
      placeholder: "\uD83D\uDEAD"
    })), m("div", {
      className: "CheckinNewHabit-actions"
    }, m(Button, {
      className: "Button",
      onclick: () => this.showNewHabitForm = false
    }, app.translator.trans('jiedh-checkin.forum.action_cancel')), m(Button, {
      className: "Button Button--primary",
      loading: this.busy,
      disabled: !this.newHabitName.trim(),
      onclick: () => this.createHabit()
    }, app.translator.trans('jiedh-checkin.forum.action_create')))));
  }

  /** 统计卡（§18.6）：当前连续 / 最长连续 / 累计成功 / 本月成功 / 成功率。 */
  renderStatsCards(habit) {
    const rate = habit.successRate();
    const cards = [{
      key: 'current',
      value: String(habit.currentStreak()),
      label: app.translator.trans('jiedh-checkin.forum.streak_label')
    }, {
      key: 'best',
      value: String(habit.bestStreak()),
      label: app.translator.trans('jiedh-checkin.forum.best_streak_label')
    }, {
      key: 'total',
      value: String(habit.totalSuccessDays()),
      label: app.translator.trans('jiedh-checkin.forum.total_label')
    }, {
      key: 'month',
      value: String(habit.monthSuccessCount()),
      label: app.translator.trans('jiedh-checkin.forum.month_label')
    }, {
      key: 'rate',
      // 无破戒（successRate 为 null）时按 100% 展示（§18.6 口径）
      value: rate === null || rate === undefined ? '100%' : Math.round(rate * 100) + '%',
      label: app.translator.trans('jiedh-checkin.forum.rate_label')
    }];
    return m("div", {
      className: "CheckinStats"
    }, cards.map(c => m("div", {
      key: c.key,
      className: 'CheckinStat CheckinStat--' + c.key
    }, m("div", {
      className: "CheckinStat-value"
    }, c.value), m("div", {
      className: "CheckinStat-label"
    }, c.label))));
  }

  /**
   * 操作条（§20.1 分享成就 + §19.4 导出数据）。
   *
   * 位置：统计卡之下、热力图之上 —— 两枚按钮都属于「对当前成就的操作」，
   * 紧贴统计数字更符合直觉。
   *
   * 显隐规则：
   *   - 「分享成就」：始终显示（纯前端能力，无后端依赖）。
   *   - 「导出数据」：仅当 config.exportEnabled 为真时显示（后端另有 403 兜底）。
   */
  renderActionsBar() {
    const config = checkinConfig();
    return m("div", {
      className: "CheckinActions"
    }, m(Button, {
      className: "Button CheckinActions-share",
      icon: "fas fa-share-alt",
      onclick: () => this.openShare()
    }, app.translator.trans('jiedh-checkin.forum.action_share')), config.exportEnabled && m(Button, {
      className: "Button CheckinActions-export",
      icon: "fas fa-download",
      onclick: () => this.exportData('csv')
    }, app.translator.trans('jiedh-checkin.forum.action_export')));
  }

  /**
   * 年度热力图卡片（§19.3，纯 CSS Grid 自绘 GitHub 风格）。
   *
   * 布局：列 = 周（从本年第一周所在的周一算起，至今天），行 = 周一…周日。
   * 固定 7 行 + 自动列（`.CheckinHeatmap-grid { grid-auto-flow: column }`），
   * 与 GitHub 贡献图一致；移动端由 less 里 `.CheckinHeatmap-scroll`
   * 提供横向滚动，不压缩格子（保证可读性）。
   */
  renderHeatmapCard() {
    const cells = this.buildHeatmapCells();
    // 本年成功天数（level > 0 即为「成功且非破戒」的格子）
    const successCount = cells.filter(c => c.level > 0).length;
    return m("div", {
      className: "CheckinHeatmap Card"
    }, m("div", {
      className: "Card-header CheckinHeatmap-head"
    }, m("h3", null, app.translator.trans('jiedh-checkin.forum.heatmap_title')), m("span", {
      className: "CheckinHeatmap-total"
    }, app.translator.trans('jiedh-checkin.forum.heatmap_total', {
      count: successCount
    }))), m("div", {
      className: "Card-body"
    }, this.loadingHeatmap ? m(LoadingIndicator, null) : cells.length === 0 || successCount === 0 ? m(Placeholder, {
      text: app.translator.trans('jiedh-checkin.forum.heatmap_empty')
    }) : m('[', null, m("div", {
      className: "CheckinHeatmap-scroll"
    }, m("div", {
      className: "CheckinHeatmap-grid"
    }, cells.map(c => this.renderHeatmapCell(c)))), m("div", {
      className: "CheckinHeatmap-foot"
    }, m("span", {
      className: "CheckinHeatmap-hint"
    }, app.translator.trans('jiedh-checkin.forum.heatmap_hint')), m("span", {
      className: "CheckinHeatmap-legend"
    }, m("span", {
      className: "CheckinHeatmap-legendLabel"
    }, app.translator.trans('jiedh-checkin.forum.heatmap_legend_less')), [0, 1, 2, 3, 4].map(lv => m("span", {
      key: lv,
      className: 'CheckinHeatmap-cell is-level-' + lv
    })), m("span", {
      className: "CheckinHeatmap-legendLabel"
    }, app.translator.trans('jiedh-checkin.forum.heatmap_legend_more')))))));
  }

  /** 单个热力格（不可点击，仅以 title 提示日期；§19.3）。 */
  renderHeatmapCell(cell) {
    const classes = ['CheckinHeatmap-cell', 'is-level-' + cell.level];
    if (!cell.inYear) classes.push('is-outside');
    if (cell.isToday) classes.push('is-today');
    if (cell.isRelapse) classes.push('is-relapse');
    if (cell.isMakeup) classes.push('is-makeup');
    return m("span", {
      key: cell.date,
      className: classes.join(' '),
      title: cell.date
    });
  }

  /** 大月历卡片（§18.2 / §9.2 纯 CSS Grid）。 */
  renderCalendarCard() {
    const cells = this.buildCalendarCells();
    const today = dayjs().format('YYYY-MM-DD');
    const todayRecord = this.monthRecords[today] ?? null;
    const checkedToday = todayRecord?.status() === STATUS_SUCCESS;
    return m("div", {
      className: "CheckinCalendar Card"
    }, m("div", {
      className: "Card-header CheckinCalendar-head"
    }, m("button", {
      className: "CheckinCalendar-nav",
      type: "button",
      "aria-label": app.translator.trans('jiedh-checkin.forum.prev_month'),
      onclick: () => this.goMonth(-1)
    }, m(Icon, {
      name: "fas fa-chevron-left"
    })), m("span", {
      className: "CheckinCalendar-month"
    }, this.monthCursor.format('YYYY / MM')), m("div", {
      className: "CheckinCalendar-headRight"
    }, m(Button, {
      className: "Button Button--link",
      onclick: () => this.goMonth(0)
    }, app.translator.trans('jiedh-checkin.forum.action_today')), m("button", {
      className: "CheckinCalendar-nav",
      type: "button",
      "aria-label": app.translator.trans('jiedh-checkin.forum.next_month'),
      onclick: () => this.goMonth(1)
    }, m(Icon, {
      name: "fas fa-chevron-right"
    })))), m("div", {
      className: "Card-body"
    }, m("div", {
      className: "CheckinCalendar-actionBar"
    }, checkedToday && checkinConfig().allowUndo ? m(Button, {
      className: "Button CheckinCalendar-undo",
      icon: "fas fa-undo",
      loading: this.busy,
      onclick: () => todayRecord && this.undoRecord(todayRecord)
    }, app.translator.trans('jiedh-checkin.forum.action_undo')) : checkedToday ? m("span", {
      className: "CheckinCalendar-done"
    }, m(Icon, {
      name: "fas fa-check"
    }), " ", app.translator.trans('jiedh-checkin.forum.action_done')) : m(Button, {
      className: "Button Button--primary CheckinCalendar-checkin",
      icon: "fas fa-check",
      loading: this.busy,
      onclick: () => this.checkin()
    }, app.translator.trans('jiedh-checkin.forum.action_checkin'))), m("div", {
      className: "CheckinCalendar-weekdays"
    }, ['weekday_mon', 'weekday_tue', 'weekday_wed', 'weekday_thu', 'weekday_fri', 'weekday_sat', 'weekday_sun'].map(k => m("span", {
      key: k,
      className: "CheckinCalendar-weekday"
    }, app.translator.trans('jiedh-checkin.forum.' + k)))), this.loadingMonth ? m(LoadingIndicator, null) : m("div", {
      className: "CheckinCalendar-grid"
    }, cells.map(cell => this.renderCalendarCell(cell)))));
  }

  /** 单个月历格（成功 = 绿点，破戒 = 浅红，补签 = 右上角小点）。 */
  renderCalendarCell(cell) {
    const classes = ['CheckinCalendar-cell'];
    if (!cell.inMonth) classes.push('is-outside');
    if (cell.isToday) classes.push('is-today');
    if (cell.isFuture) classes.push('is-future');
    const status = cell.record ? cell.record.status() : null;
    if (status === STATUS_SUCCESS) classes.push('is-success');else if (status !== null) classes.push('is-relapse');
    if (cell.record?.isMakeup()) classes.push('is-makeup');
    if (this.activeDate === cell.date) classes.push('is-active');
    return m("button", {
      key: cell.date,
      type: "button",
      className: classes.join(' '),
      "aria-label": cell.date,
      disabled: cell.isFuture && !cell.record,
      onclick: () => this.openDay(cell)
    }, m("span", {
      className: "CheckinCalendar-day"
    }, cell.day));
  }

  /**
   * 点击某日：展开「当日详情」。
   * 未来日期（且无记录）不响应。
   */
  openDay(cell) {
    if (cell.isFuture && !cell.record) return;
    this.activeDate = cell.date;
    this.detailMood = cell.record?.mood() ?? 0;
    this.detailNote = cell.record?.note() ?? '';
    m.redraw();
  }

  /** 当日详情面板：查看/编辑心情、备注；删除（撤销）记录。 */
  renderDayDetail() {
    const date = this.activeDate;
    const record = this.monthRecords[date] ?? null;
    return m("div", {
      className: "CheckinDayDetail Card"
    }, m("div", {
      className: "Card-header"
    }, m("span", {
      className: "CheckinDayDetail-date"
    }, date), m("button", {
      className: "CheckinDayDetail-close",
      type: "button",
      onclick: () => this.activeDate = null,
      "aria-label": app.translator.trans('jiedh-checkin.forum.close')
    }, m(Icon, {
      name: "fas fa-times"
    }))), m("div", {
      className: "Card-body"
    }, record ? m('[', null, m("div", {
      className: "CheckinDayDetail-status"
    }, record.status() === STATUS_SUCCESS ? m("span", {
      className: "CheckinBadge CheckinBadge--success"
    }, app.translator.trans('jiedh-checkin.forum.status_success')) : m("span", {
      className: "CheckinBadge CheckinBadge--relapse"
    }, app.translator.trans('jiedh-checkin.forum.status_relapse')), record.isMakeup() && m("span", {
      className: "CheckinBadge CheckinBadge--makeup"
    }, app.translator.trans('jiedh-checkin.forum.badge_makeup'))), m("label", {
      className: "CheckinDayDetail-field"
    }, m("span", null, app.translator.trans('jiedh-checkin.forum.detail_mood')), m("select", {
      className: "FormControl",
      value: String(this.detailMood),
      onchange: e => this.detailMood = Number(e.target.value)
    }, m("option", {
      value: "0"
    }, app.translator.trans('jiedh-checkin.forum.mood_unset')), MOOD_VALUES.map(m => m("option", {
      key: m,
      value: String(m)
    }, m)))), m("label", {
      className: "CheckinDayDetail-field"
    }, m("span", null, app.translator.trans('jiedh-checkin.forum.detail_note')), m("textarea", {
      className: "FormControl",
      rows: 3,
      maxlength: 200,
      value: this.detailNote,
      oninput: e => this.detailNote = e.target.value
    })), m("div", {
      className: "CheckinDayDetail-actions"
    }, m(Button, {
      className: "Button CheckinDayDetail-remove",
      icon: "fas fa-trash",
      loading: this.busy,
      onclick: () => this.undoRecord(record)
    }, app.translator.trans('jiedh-checkin.forum.action_remove')), m(Button, {
      className: "Button Button--primary",
      loading: this.busy,
      onclick: () => this.saveDetail(record)
    }, app.translator.trans('jiedh-checkin.forum.action_save')))) : m("div", {
      className: "CheckinDayDetail-empty"
    }, m("p", null, app.translator.trans('jiedh-checkin.forum.detail_no_record')), date === dayjs().format('YYYY-MM-DD') && m(Button, {
      className: "Button Button--primary",
      loading: this.busy,
      onclick: () => this.checkin()
    }, app.translator.trans('jiedh-checkin.forum.action_checkin')))));
  }

  /** 记录时间线卡片（§18.5）：倒序 + 分页。 */
  renderTimelineCard() {
    return m("div", {
      className: "CheckinTimeline Card"
    }, m("div", {
      className: "Card-header"
    }, m("h3", null, app.translator.trans('jiedh-checkin.forum.timeline_title'))), m("div", {
      className: "Card-body"
    }, this.loadingRecords ? m(LoadingIndicator, null) : this.records.length === 0 ? m(Placeholder, {
      text: app.translator.trans('jiedh-checkin.forum.timeline_empty')
    }) : m("ul", {
      className: "CheckinTimeline-list"
    }, this.records.map(r => this.renderTimelineItem(r))), this.renderPager()));
  }

  /**
   * 时间线分页条（自绘，零核心依赖 —— 原因见文件头「为什么不用核心 Pagination」）。
   * 仅在超过一页时渲染；形态与 packages/review 的 ReviewList.pager() 一致。
   * onChange 语义与核心 Pagination 对齐：回调传出的是**目标页码**（从 1 开始）。
   */
  renderPager() {
    const total = this.recordsTotal;
    const perPage = PAGE_SIZE;

    // 只有超过一页才需要分页条
    if (!total || total <= perPage) return null;
    const current = this.page;
    const pages = Math.max(1, Math.ceil(total / perPage));
    return m("div", {
      className: "CheckinTimeline-pager"
    }, m(Button, {
      className: "Button CheckinTimeline-pagerBtn",
      disabled: current <= 1,
      onclick: () => this.goToPage(current - 1)
    }, app.translator.trans('jiedh-checkin.forum.pager_prev')), m("span", {
      className: "CheckinTimeline-pagerInfo"
    }, app.translator.trans('jiedh-checkin.forum.pager_info', {
      current,
      total: pages
    })), m(Button, {
      className: "Button CheckinTimeline-pagerBtn",
      disabled: current >= pages,
      onclick: () => this.goToPage(current + 1)
    }, app.translator.trans('jiedh-checkin.forum.pager_next')));
  }

  /** 单条时间线记录：日期 + 状态徽章 + 心情 + 备注（折叠）。 */
  renderTimelineItem(record) {
    const note = record.note();
    const mood = record.mood();
    return m("li", {
      key: record.id(),
      className: 'CheckinTimeline-item' + (record.status() === STATUS_SUCCESS ? ' is-success' : ' is-relapse')
    }, m("div", {
      className: "CheckinTimeline-main"
    }, m("span", {
      className: "CheckinTimeline-date"
    }, record.checkinDate()), record.status() === STATUS_SUCCESS ? m("span", {
      className: "CheckinBadge CheckinBadge--success"
    }, app.translator.trans('jiedh-checkin.forum.status_success')) : m("span", {
      className: "CheckinBadge CheckinBadge--relapse"
    }, app.translator.trans('jiedh-checkin.forum.status_relapse')), record.isMakeup() && m("span", {
      className: "CheckinBadge CheckinBadge--makeup"
    }, app.translator.trans('jiedh-checkin.forum.badge_makeup')), mood ? m("span", {
      className: "CheckinTimeline-mood"
    }, app.translator.trans('jiedh-checkin.forum.detail_mood'), ": ", mood) : null), note && m("p", {
      className: "CheckinTimeline-note"
    }, note.length > 80 ? note.slice(0, 80) + '…' : note));
  }

  // =========================================================================
  // 工具方法
  // =========================================================================

  /** 当前选中目标模型。 */
  currentHabit() {
    if (!this.currentHabitId) return null;
    return this.habits.find(h => Number(h.id()) === this.currentHabitId) ?? null;
  }

  /** 目标展示名（图标 + 名称）。 */
  renderHabitLabel(habit) {
    return (habit.icon() ? habit.icon() + ' ' : '') + habit.name();
  }

  /**
   * 生成月历 42 格（周一为首日，§9.2）。
   *
   * 偏移算法：(start.getDay() + 6) % 7 —— 把 JS 的「周日 = 0」转成「周一 = 0」。
   * 网格 = 起始偏移日往前推 offset 天起步，共 6 行 × 7 列 = 42 格。
   */
  buildCalendarCells() {
    const start = this.monthCursor.startOf('month');
    const offset = (start.day() + 6) % 7;
    const gridStart = start.subtract(offset, 'day');
    const today = dayjs().format('YYYY-MM-DD');
    const now = dayjs();
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = gridStart.add(i, 'day');
      const date = d.format('YYYY-MM-DD');
      cells.push({
        date,
        day: d.date(),
        inMonth: d.month() === start.month(),
        isToday: date === today,
        isFuture: d.isAfter(now, 'day'),
        record: this.monthRecords[date] ?? null
      });
    }
    return cells;
  }

  /**
   * 生成年度热力图格子序列（§19.3，纯前端自绘 GitHub 风格）。
   *
   * 网格规则（与 GitHub 贡献图一致）：
   *   - 行 = 周一…周日（7 行固定）；列 = 周（自动列，`grid-auto-flow: column`）。
   *   - 起点：本年 1 月 1 日所在周的**周一**（可能落在上一年，这些补位格
   *     标记 inYear=false 并淡化显示）。
   *   - 终点：**今天**（未来留白不渲染，避免大片空格）。
   *
   * 热力等级（level，0–4）：按「截至当日的连续成功天数」分桶 ——
   *   0：当日无成功记录（含无记录 / 破戒）
   *   1：连续 1–2 天
   *   2：连续 3–6 天
   *   3：连续 7–29 天
   *   4：连续 ≥30 天
   * 分桶阈值与 §19.3「5 级热力渐变」一致；破戒日单独以 isRelapse 标红。
   */
  buildHeatmapCells() {
    const now = dayjs();
    const today = now.format('YYYY-MM-DD');
    const year = now.year();

    // 起点对齐到「本年 1 月 1 日所在周的周一」（周一 = 0 → (day()+6)%7）
    const yearStart = now.startOf('year');
    const offset = (yearStart.day() + 6) % 7;
    const gridStart = yearStart.subtract(offset, 'day');
    const cells = [];
    let run = 0; // 「截至当日」的连续成功计数（遇破戒/无记录归零）

    for (let d = gridStart; !d.isAfter(now, 'day'); d = d.add(1, 'day')) {
      const date = d.format('YYYY-MM-DD');
      const inYear = d.year() === year;
      const record = this.yearRecords[date] ?? null;
      const isSuccess = record?.status() === STATUS_SUCCESS;

      // 维护连续计数：成功 +1；破戒或（本年但无记录）归零
      if (isSuccess) {
        run += 1;
      } else {
        run = 0;
      }

      // 分桶：只有成功日才有热力等级（run 此时 = 连续天数）
      // 口径与分享海报共用 heatmapLevelForRun()，切勿在此另写一套阈值
      const level = isSuccess ? heatmapLevelForRun(run) : 0;
      cells.push({
        date,
        inYear,
        isToday: date === today,
        level,
        isRelapse: record != null && !isSuccess,
        isMakeup: record?.isMakeup() === true,
        hasRecord: record != null
      });
    }
    return cells;
  }

  /**
   * 组装「年度热力等级数组」（下标 = 该年第几天，0 基），供分享海报绘制年度热力图。
   *
   * 与 `buildHeatmapCells()` 的关系：同一份 `yearRecords`、同一个
   * `heatmapLevelForRun()` 口径，但**只保留海报绘制所需的两个维度** ——
   * 数组长度（365 / 366）与等级（0–4），不带日期字符串 / 是否今天 / 是否补签等
   * 仅网页交互才需要的字段（海报是离线图片，传过去只会变成无用载荷）。
   *
   * 与网页版热力图的两点**有意差异**（已记入设计文档）：
   *   ① 起点固定为 **1 月 1 日**（网页版从「1 月 1 日所在周的周一」开始画，
   *      首尾会补几位上一年 / 下一年的格子，用于对齐星期）。海报上不显示星期，
   *      按自然日平铺即可，同时让数组长度严格等于 365/366，契约更简单。
   *   ② **今天之后**的日期补 0（与「未打卡」同色）。这样无论 1 月还是 12 月分享，
   *      海报上都是一张「整年」网格，版式稳定；否则 1 月分享会得到一条细长网格。
   *
   * 数据未就绪（`yearRecords` 尚在加载 / 加载失败）时返回**全 0** 数组；
   * share 侧对「全 0」的处理是**整块不绘制**（而不是画一张空网格），
   * 因此不会出现「错误的年历」，也不会因为分享过早而画出误导性图形。
   *
   * @returns 长度 365 / 366 的热力等级数组（0–4）
   */
  buildYearHeatmapLevels() {
    const now = dayjs();
    const year = now.year();
    // 闰年判定用标准格里高利规则，**刻意不用 dayjs.isLeapYear()** ——
    // 该 API 属 dayjs 插件，本项目未 extend（扩展侧不引 dayjs 依赖，见文件头说明）。
    const isLeap = year % 4 === 0 && year % 100 !== 0 || year % 400 === 0;
    const total = isLeap ? 366 : 365;
    const levels = new Array(total).fill(0);

    // 「截至当日」的连续成功计数（口径同 buildHeatmapCells）
    let run = 0;
    let cursor = now.startOf('year');
    for (let i = 0; i < total; i += 1) {
      // 未来日期直接跳出：levels 已初始化为 0，即与「未打卡」同色
      if (cursor.isAfter(now, 'day')) break;
      const record = this.yearRecords[cursor.format('YYYY-MM-DD')] ?? null;
      if (record?.status() === STATUS_SUCCESS) {
        run += 1;
        levels[i] = heatmapLevelForRun(run);
      } else {
        run = 0;
      }
      cursor = cursor.add(1, 'day');
    }
    return levels;
  }

  /**
   * 统一错误文案提取。
   * JSON:API / forum 信封的失败都会经 app.request 抛出，错误 detail 位于
   * `e.errors[0].detail`（核心 RequestError），取不到则回落为通用网络错误文案。
   */
  errorMessage(e) {
    const detail = e?.errors?.[0]?.detail ?? e?.response?.errors?.[0]?.detail;
    if (detail && typeof detail === 'string') return detail;
    return extractText(app.translator.trans('jiedh-checkin.forum.toast_network_error'));
  }
}

/**
 * 独立页 `/checkin` 的 Page 壳（薄壳，无业务逻辑）。
 *
 * ★ 职责边界（方案 A，2026-09-30）★
 *   - Page 基类负责：注册路由上下文（app.current）、bodyClass、滚动复位；
 *   - 本壳负责：补齐独立页的外层布局 `.CheckinPage > .container`（less/forum.less
 *     的嵌套选择器 `.CheckinPage .CheckinHeader` 等依赖这层），再挂载 CheckinContent。
 *
 * ★ 与个人主页 tab 的关系 ★
 *   `/u/:username/checkin` 走的是 UserCheckinPage（extends UserPage），
 *   同样挂载 CheckinContent；两处共用同一份业务逻辑，互不重复。
 */
export default class CheckinPage extends Page {
  view() {
    return m("div", {
      className: "CheckinPage"
    }, m("div", {
      className: "container"
    }, m(CheckinContent, null)));
  }
}
flarum.reg.add('jiedh-checkin', 'forum/components/CheckinPage', CheckinPage);flarum.reg.addChunkModule('956', '68', 'jiedh-checkin', 'forum/components/CheckinPostConfirmModal');
flarum.reg.addChunkModule('19', '909', 'jiedh-checkin', 'forum/components/CheckinDirectPostModal');